/**
 * Page édito dédiée à la campagne nationale 2026 de la Sécurité routière
 * « À moto, ne soyez pas victime de votre passion ».
 *
 * Route : /campagne-2026-motards-experimentes
 *
 * Objectif : transformer la campagne vidéo en pièce éditoriale Code 2 Roues
 * (analyse du chiffre 72 %, mise en perspective pour nos élèves motards et
 * pour les enseignants ECSR). Crédit intégral à la Sécurité routière.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Newspaper, Clock, Play, ExternalLink,
  AlertTriangle, GraduationCap, BookOpen, Heart, ShieldCheck,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CURRENT_CAMPAIGN } from '../data/officialVideos';
import useSeo from '../hooks/useSeo';

function YouTubeEmbed({ videoId, title, autoplay = false }) {
  const [activated, setActivated] = useState(autoplay);
  const thumbnail = `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`;
  const src = `https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1${autoplay ? '&autoplay=1' : ''}`;

  if (!activated) {
    return (
      <button
        type="button"
        onClick={() => setActivated(true)}
        className="relative group w-full aspect-video rounded-2xl overflow-hidden border border-border shadow-lg bg-black focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary))]"
        data-testid={`youtube-poster-${videoId}`}
        aria-label={`Lire la vidéo : ${title}`}
      >
        <img
          src={thumbnail}
          alt={title}
          loading="lazy"
          className="w-full h-full object-cover opacity-70 group-hover:opacity-90 transition-opacity"
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-[hsl(var(--primary))] flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
            <Play className="w-7 h-7 sm:w-8 sm:h-8 text-white fill-white ml-1" />
          </div>
        </div>
        <div className="absolute bottom-3 left-3 right-3 text-left">
          <div className="text-[10px] font-black uppercase tracking-widest text-white/80">Lecture YouTube</div>
          <div className="text-sm sm:text-base font-bold text-white drop-shadow line-clamp-2">{title}</div>
        </div>
      </button>
    );
  }

  return (
    <div className="relative w-full aspect-video rounded-2xl overflow-hidden border border-border shadow-lg bg-black">
      <iframe
        src={src}
        title={title}
        loading="lazy"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="absolute inset-0 w-full h-full"
        data-testid={`youtube-iframe-${videoId}`}
      />
    </div>
  );
}

export default function CampagneMotardsExperimentes() {
  useSeo({
    title: `${CURRENT_CAMPAIGN.title} — Campagne Sécurité routière 2026 · Code 2 Roues`,
    description:
      "Décryptage de la campagne 2026 de la Sécurité routière sur les motards expérimentés. 72 % des motards tués étaient expérimentés, sur moto lourde, en trajet loisir. Pourquoi, et que faire ?",
    path: `/${CURRENT_CAMPAIGN.slug}`,
    keywords:
      'campagne sécurité routière 2026 moto, motards expérimentés, accident moto loisir, sécurité moto, à moto ne soyez pas victime',
  });

  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); }, []);

  return (
    <article className="min-h-screen bg-background pb-16" data-testid="campagne-2026-page">
      {/* HEADER MAGAZINE */}
      <header
        className="relative overflow-hidden border-b"
        style={{
          background:
            'radial-gradient(ellipse 70% 60% at 100% 0%, rgba(207,32,49,0.12), transparent 60%), radial-gradient(ellipse 50% 40% at 0% 100%, rgba(8,145,178,0.06), transparent 60%), hsl(var(--card))',
        }}
      >
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage:
              'repeating-linear-gradient(-45deg, transparent 0 14px, hsl(var(--foreground)) 14px 15px)',
          }}
        />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-xs sm:text-sm text-muted-foreground hover:text-foreground mb-5"
            data-testid="back-home-link"
          >
            <ArrowLeft className="w-4 h-4" /> Retour à l'accueil
          </Link>

          <div className="flex flex-wrap items-center gap-2 mb-4">
            <Badge className="bg-primary text-primary-foreground border-none text-[10px]">
              Nouvelle campagne
            </Badge>
            <span className="text-[10px] sm:text-xs uppercase tracking-[0.18em] font-bold text-primary">
              {CURRENT_CAMPAIGN.kicker}
            </span>
          </div>

          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.05] mb-4 max-w-4xl"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            data-testid="campagne-title"
          >
            {CURRENT_CAMPAIGN.title}
          </h1>
          <p className="text-base sm:text-lg lg:text-xl text-foreground/75 leading-relaxed max-w-3xl">
            {CURRENT_CAMPAIGN.subtitle}
          </p>

          <div className="mt-5 flex flex-wrap items-center gap-2 sm:gap-3 text-[11px] sm:text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md border bg-background/60">
              <Newspaper className="w-3.5 h-3.5" /> Décryptage Code 2 Roues
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md border bg-background/60">
              <Clock className="w-3.5 h-3.5" /> 5 min de lecture
            </span>
            <a
              href={CURRENT_CAMPAIGN.channelUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md border bg-background/60 hover:text-foreground"
              data-testid="campagne-source-link"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Source : {CURRENT_CAMPAIGN.channel}
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 pt-8">
        {/* LECTEUR VERSION LONGUE */}
        <section className="mb-8" data-testid="campagne-hero-video">
          <YouTubeEmbed
            videoId={CURRENT_CAMPAIGN.heroVideoId}
            title={`${CURRENT_CAMPAIGN.title} — version longue (${CURRENT_CAMPAIGN.heroVideoDuration})`}
          />
          <div className="mt-2 text-xs text-muted-foreground italic text-center">
            Film officiel · {CURRENT_CAMPAIGN.heroVideoDuration} · Réalisation : {CURRENT_CAMPAIGN.directors}
          </div>
        </section>

        {/* ÉDITO */}
        <section className="rounded-xl border bg-card/50 p-5 sm:p-6 mb-2" data-testid="campagne-edito">
          <div className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.18em] text-primary mb-2">
            Édito
          </div>
          <h2
            className="text-xl sm:text-2xl font-bold mb-3"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            La passion ne protège pas — et c'est précisément le sujet
          </h2>
          <p className="text-sm sm:text-base leading-relaxed text-foreground/85">
            Pendant longtemps, la prévention routière s'est concentrée sur les jeunes permis : 17 ans,
            scooter, premier accident, leçon apprise dans la douleur. C'est utile, c'est nécessaire,
            mais ça ne raconte qu'un bout de l'histoire. <strong>Cette campagne 2026 vient combler
            l'autre bout</strong>, celui dont on parle moins : les motards <em>expérimentés</em>, ceux qui
            ont des années de selle, une moto lourde, et qui meurent en revenant d'une virée.
          </p>
          <div className="text-xs italic text-muted-foreground mt-3">— La rédaction de Code 2 Roues</div>
        </section>

        {/* CHIFFRE CLÉ */}
        <aside
          className="my-8 sm:my-10 rounded-xl p-6 sm:p-8 text-center relative overflow-hidden"
          style={{
            background: 'radial-gradient(ellipse at center, rgba(207,32,49,0.18), transparent 70%)',
            border: '1px solid rgba(207,32,49,0.30)',
          }}
          data-testid="campagne-key-stat"
        >
          <div className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.18em] mb-2 text-[hsl(var(--primary))]">
            Le chiffre qui change la lecture
          </div>
          <div className="text-5xl sm:text-7xl font-black tabular-nums leading-none text-[hsl(var(--primary))]">
            72 %
          </div>
          <div className="text-sm sm:text-base font-bold mt-2">des motards tués sont des usagers expérimentés</div>
          <div className="text-xs sm:text-sm text-foreground/70 mt-1 max-w-md mx-auto">
            sur moto lourde, lors d'un trajet de loisir — pas un trajet domicile-travail, pas un débutant
          </div>
          <div className="text-[10px] text-muted-foreground italic mt-3">
            Source : Sécurité routière, dossier de campagne 2026
          </div>
        </aside>

        {/* CORPS ÉDITO */}
        <section className="my-10" data-testid="campagne-body-1">
          <h2
            className="text-2xl sm:text-3xl font-extrabold tracking-tight leading-tight mb-3 flex items-start gap-2"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            <AlertTriangle className="w-6 h-6 mt-1 text-primary flex-shrink-0" />
            <span>L'expérience comme angle mort</span>
          </h2>
          <div className="space-y-3 text-[15px] sm:text-base leading-relaxed text-foreground/90">
            <p>
              Le paradoxe de Julien — le motard du film — c'est qu'il <strong>sait</strong>. Il sait
              que la vitesse est le danger numéro un. Il sait que le carrefour est l'endroit où on
              meurt. Il le sait <em>tellement bien</em> qu'il a fini par croire que cette connaissance
              suffisait. C'est le piège.
            </p>
            <p>
              L'expérience à moto fonctionne par couches : les premiers mois, on roule prudemment
              parce qu'on a peur. Vers 5 000 km, on roule mieux parce qu'on commence à anticiper.
              Vers 20 000 km, on roule <strong>plus vite</strong> — parce qu'on a confiance dans nos
              gestes, dans notre machine, dans notre lecture du flux. Et c'est dans cette zone, entre
              30 et 50 ans, entre la sportive du week-end et le trail bien chargé, que la statistique
              fauche le plus.
            </p>
          </div>
        </section>

        {/* ENCADRÉ "À RETENIR" */}
        <aside
          className="rounded-xl border-l-4 my-6 sm:my-8 p-4 sm:p-5"
          style={{ borderColor: '#0891b2', background: 'rgba(8,145,178,0.06)' }}
          data-testid="campagne-callout-routine"
        >
          <div className="flex items-center gap-2 mb-2">
            <Heart className="w-4 h-4" style={{ color: '#0891b2' }} />
            <span
              className="text-[11px] font-black uppercase tracking-[0.15em]"
              style={{ color: '#0891b2' }}
            >
              Le rituel qui vous trahit
            </span>
          </div>
          <div className="text-sm sm:text-base leading-relaxed text-foreground/90">
            Dans le film, Julien dit qu'il embrasse ses enfants <em>avant chaque sortie</em>. C'est un
            geste d'amour, c'est aussi un signal : il sait qu'il peut ne pas rentrer. La campagne ne
            culpabilise pas ce ressenti — elle dit l'inverse. <strong>Vivre une passion, c'est d'abord
            ne pas en être victime.</strong> Et la transmettre, c'est transmettre aussi ce qu'elle
            exige : la lucidité, pas seulement le plaisir.
          </div>
        </aside>

        {/* DEUXIÈME PARTIE */}
        <section className="my-10" data-testid="campagne-body-2">
          <h2
            className="text-2xl sm:text-3xl font-extrabold tracking-tight leading-tight mb-3 flex items-start gap-2"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            <ShieldCheck className="w-6 h-6 mt-1 text-primary flex-shrink-0" />
            <span>Pourquoi on aime ce film</span>
          </h2>
          <div className="space-y-3 text-[15px] sm:text-base leading-relaxed text-foreground/90">
            <p>
              Réalisé par <strong>Alban Teurlay et Thierry Demaizière</strong> (les documentaristes
              derrière <em>Lourdes</em> et <em>Sapeurs-Pompiers de Paris</em>), le film fait le pari
              du portrait. Pas de scène d'accident, pas de slogan asséné — juste un homme qui dit
              tout haut ce que beaucoup de motards pensent tout bas. C'est rare. C'est juste. Et ça
              parle <strong>aux motards par un motard</strong>, ce qui change tout par rapport aux
              campagnes plus frontales du passé.
            </p>
            <p>
              Pour nous, Code 2 Roues, cette campagne touche un point essentiel : <strong>le code
              moto ne s'arrête pas au permis</strong>. La vraie formation continue à 35 ans, sur le
              trajet de loisir, dans le virage qu'on connaît par cœur — celui où l'on aborde un peu
              plus vite parce qu'on le « tient ». La sécurité routière ne se révise pas une fois,
              elle se réactualise toute une vie.
            </p>
          </div>

          {/* À RETENIR */}
          <div className="mt-5 rounded-lg border bg-card/40 p-4">
            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">
              À retenir
            </div>
            <ul className="space-y-1.5">
              <li className="text-sm flex gap-2">
                <span className="text-emerald-600 mt-0.5 flex-shrink-0">›</span>
                <span>L'expérience à moto ne protège pas — elle peut endormir la vigilance.</span>
              </li>
              <li className="text-sm flex gap-2">
                <span className="text-emerald-600 mt-0.5 flex-shrink-0">›</span>
                <span>Le trajet de loisir concentre la majorité des accidents mortels chez les motards expérimentés.</span>
              </li>
              <li className="text-sm flex gap-2">
                <span className="text-emerald-600 mt-0.5 flex-shrink-0">›</span>
                <span>Le danger n°1 reste la vitesse, devant les carrefours et la perte de contrôle en virage.</span>
              </li>
              <li className="text-sm flex gap-2">
                <span className="text-emerald-600 mt-0.5 flex-shrink-0">›</span>
                <span>Transmettre la moto à ses enfants, c'est transmettre <em>les exigences</em> qu'elle impose, pas seulement le plaisir.</span>
              </li>
            </ul>
          </div>
        </section>

        {/* VERSION COURTE */}
        <section className="my-10" data-testid="campagne-short-video">
          <h2
            className="text-xl sm:text-2xl font-bold mb-3"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Version courte du spot ({CURRENT_CAMPAIGN.shortVideoDuration})
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            Format diffusion TV / réseaux sociaux. Utile pour partager rapidement la campagne, ou la
            projeter en début de session de formation.
          </p>
          <YouTubeEmbed
            videoId={CURRENT_CAMPAIGN.shortVideoId}
            title={`${CURRENT_CAMPAIGN.title} — version courte (${CURRENT_CAMPAIGN.shortVideoDuration})`}
          />
        </section>

        {/* ENCADRÉ ECSR */}
        <aside
          className="my-10 rounded-xl border-2 border-dashed border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/5 p-5 sm:p-6"
          data-testid="campagne-ecsr"
        >
          <div className="flex items-center gap-2 mb-3">
            <GraduationCap className="w-4 h-4 text-[hsl(var(--primary))]" />
            <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.18em] text-[hsl(var(--primary))]">
              Côté formateur ECSR
            </span>
          </div>
          <p className="text-sm sm:text-base leading-relaxed text-foreground/85 mb-4">
            Cette campagne est un excellent point d'appui pédagogique pour les sessions de
            sensibilisation moto, notamment auprès des élèves passerelle A2 → A et des stages
            post-permis. Le format portrait évite l'écueil du « film choc » qui braque
            instinctivement les adultes motards.
          </p>
          <ul className="space-y-2 mb-4">
            <li className="flex items-start gap-2 text-sm text-foreground/80">
              <span className="text-[hsl(var(--primary))] font-bold mt-0.5">›</span>
              <span>Projection en début de session (1:50) puis tour de table : « Qu'avez-vous reconnu de vos propres habitudes ? »</span>
            </li>
            <li className="flex items-start gap-2 text-sm text-foreground/80">
              <span className="text-[hsl(var(--primary))] font-bold mt-0.5">›</span>
              <span>Croisement avec les statistiques ONISR : profils des tués 2024-2025 sur deux-roues motorisés.</span>
            </li>
            <li className="flex items-start gap-2 text-sm text-foreground/80">
              <span className="text-[hsl(var(--primary))] font-bold mt-0.5">›</span>
              <span>Travail sur la transmission père-enfant / passager : le film ouvre cette discussion sans la forcer.</span>
            </li>
          </ul>
          <Button asChild variant="outline" size="sm" className="gap-2">
            <Link to="/apprendre/conducteur" data-testid="campagne-ecsr-conducteur-link">
              <BookOpen className="w-4 h-4" />
              Voir aussi le dossier « Le conducteur »
              <ArrowRight className="w-4 h-4" />
            </Link>
          </Button>
        </aside>

        {/* CTA RETOUR */}
        <div className="mt-12 p-6 rounded-2xl bg-muted/30 border border-border text-center">
          <Newspaper className="w-8 h-8 text-[hsl(var(--primary))] mx-auto mb-3" />
          <h3 className="text-base font-black mb-1">Continuer le Magazine pédagogique</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Décodez la route, les usagers et la mécanique en 9 numéros gratuits.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <Button asChild variant="outline">
              <Link to="/" data-testid="campagne-back-home">
                <ArrowLeft className="w-4 h-4 mr-1.5" /> Retour à l'accueil
              </Link>
            </Button>
            <Button asChild data-testid="campagne-goto-magazine">
              <Link to="/revision">
                Lire le Magazine <ArrowRight className="w-4 h-4 ml-1.5" />
              </Link>
            </Button>
          </div>
        </div>

        {/* CRÉDITS */}
        <div className="mt-8 text-[11px] text-muted-foreground text-center leading-relaxed">
          <strong>Crédits vidéo</strong> · © {CURRENT_CAMPAIGN.channel} · Réalisation : {CURRENT_CAMPAIGN.directors}
          <br />
          Vidéos intégrées via l'API officielle YouTube (mode <code>youtube-nocookie</code>) — aucune
          ré-exploitation commerciale, contenu hébergé sur la chaîne officielle de la Délégation à la
          Sécurité routière.
        </div>
      </main>
    </article>
  );
}
